import { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/Select';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePayrollStore, PayrollRecord } from '../../../store/payrollStore';
import { useEmployeeStore } from '../../../store/employeeStore';

const money = (n: number) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
const sumVals = (o: Record<string, any>) => Object.values(o || {}).reduce((s: number, v) => s + (Number(v) || 0), 0);

const EMPTY_FORM = {
    employeeId: '', periodStart: '', periodEnd: '', basicSalary: '',
    housing: '', transport: '', allowOther: '',
    tax: '', insurance: '', retirement: '', dedOther: '',
    notes: '',
};

export function PayrollView() {
    const { records, stats, isLoading, fetchPayroll, fetchStats, generatePayroll, approvePayroll, markPaid } = usePayrollStore();
    const { employees, fetchEmployees } = useEmployeeStore();

    const [isFormOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [slip, setSlip] = useState<PayrollRecord | null>(null);
    const [payTarget, setPayTarget] = useState<PayrollRecord | null>(null);
    const [payForm, setPayForm] = useState({ paymentDate: '', paymentReference: '' });
    const [paying, setPaying] = useState(false);
    const [actingId, setActingId] = useState<string | null>(null);

    useEffect(() => {
        fetchPayroll();
        fetchStats();
        fetchEmployees({ limit: 200 });
    }, []);

    const empById = useMemo(() => {
        const m = new Map<string, any>();
        for (const e of employees) m.set(e.id, e);
        return m;
    }, [employees]);

    const nameFor = (id: string) => {
        const e = empById.get(id);
        return e ? (e.displayName || `${e.firstName} ${e.lastName}`) : 'Unknown employee';
    };

    // Live gross/net preview for the generate form.
    const preview = useMemo(() => {
        const basic = Number(form.basicSalary) || 0;
        const allow = (Number(form.housing) || 0) + (Number(form.transport) || 0) + (Number(form.allowOther) || 0);
        const ded = (Number(form.tax) || 0) + (Number(form.insurance) || 0) + (Number(form.retirement) || 0) + (Number(form.dedOther) || 0);
        const gross = basic + allow;
        return { gross, net: gross - ded };
    }, [form]);

    const openGenerate = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setFormOpen(true);
    };

    const onEmployeeChange = (v: string) => {
        const emp = empById.get(v);
        setForm((f) => ({ ...f, employeeId: v, basicSalary: emp?.salary != null ? String(emp.salary) : f.basicSalary }));
    };

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.employeeId) next.employeeId = 'Select an employee';
        if (!form.periodStart) next.periodStart = 'Start date is required';
        if (!form.periodEnd) next.periodEnd = 'End date is required';
        if (form.periodStart && form.periodEnd && form.periodStart > form.periodEnd) next.periodEnd = 'End must be after start';
        if (!form.basicSalary || Number(form.basicSalary) < 0) next.basicSalary = 'Enter a valid basic salary';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleGenerate = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const allowances = {
                housing: Number(form.housing) || 0,
                transport: Number(form.transport) || 0,
                other: Number(form.allowOther) || 0,
            };
            const deductions = {
                tax: Number(form.tax) || 0,
                insurance: Number(form.insurance) || 0,
                retirement: Number(form.retirement) || 0,
                other: Number(form.dedOther) || 0,
            };
            await generatePayroll({
                employeeId: form.employeeId,
                periodStart: form.periodStart,
                periodEnd: form.periodEnd,
                basicSalary: Number(form.basicSalary),
                allowances,
                deductions,
                grossSalary: preview.gross,
                netSalary: preview.net,
                notes: form.notes.trim() || undefined,
            });
            toast.success('Payroll generated');
            setFormOpen(false);
        } catch (e) {
            toast.error('Failed to generate payroll');
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async (id: string) => {
        setActingId(id);
        try {
            await approvePayroll(id);
            toast.success('Payroll approved');
        } catch (e) {
            toast.error('Failed to approve payroll');
        } finally {
            setActingId(null);
        }
    };

    const openMarkPaid = (r: PayrollRecord) => {
        setPayTarget(r);
        setPayForm({ paymentDate: new Date().toISOString().slice(0, 10), paymentReference: `PAY-${Date.now()}` });
    };

    const handleMarkPaid = async () => {
        if (!payTarget) return;
        setPaying(true);
        try {
            await markPaid(payTarget.id, payForm);
            toast.success('Marked as paid');
            setPayTarget(null);
        } catch (e) {
            toast.error('Failed to mark as paid');
        } finally {
            setPaying(false);
        }
    };

    const statusVariant = (s: string) => (s === 'paid' ? 'success' : s === 'processed' ? 'info' : s === 'cancelled' ? 'secondary' : 'warning');

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Payroll</h1>
                    <p className="text-muted-foreground mt-1">Generate, approve, and disburse employee payroll</p>
                </div>
                <Button variant="primary" onClick={openGenerate}>
                    <Plus className="w-4 h-4 mr-2" />
                    Generate Payroll
                </Button>
            </div>

            {/* Real stats (current month) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card><CardContent className="p-4">
                    <p className="text-xs text-muted-foreground font-semibold mb-1">Total Net (this month)</p>
                    <p className="text-foreground font-display text-metric">{money(stats?.total.amount ?? 0)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{stats?.total.count ?? 0} records</p>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <p className="text-xs text-muted-foreground font-semibold mb-1">Draft</p>
                    <p className="text-foreground font-display text-metric">{stats?.draft.count ?? 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">{money(stats?.draft.amount ?? 0)}</p>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <p className="text-xs text-muted-foreground font-semibold mb-1">Approved</p>
                    <p className="text-foreground font-display text-metric">{stats?.processed.count ?? 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">{money(stats?.processed.amount ?? 0)}</p>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <p className="text-xs text-muted-foreground font-semibold mb-1">Paid</p>
                    <p className="text-foreground font-display text-metric">{stats?.paid.count ?? 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">{money(stats?.paid.amount ?? 0)}</p>
                </CardContent></Card>
            </div>

            {/* Payroll Register */}
            <Card>
                <CardHeader><h3 className="text-foreground font-display text-section-title">Payroll Register</h3></CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left min-w-[820px]">
                                <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                                    <tr>
                                        <th className="px-6 py-3">Employee</th>
                                        <th className="px-4 py-3">Period</th>
                                        <th className="px-4 py-3 text-right">Gross</th>
                                        <th className="px-4 py-3 text-right">Deductions</th>
                                        <th className="px-4 py-3 text-right">Net</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-6 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {records.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">
                                                No payroll records yet. Generate the first run.
                                            </td>
                                        </tr>
                                    ) : records.map((r) => (
                                        <tr key={r.id} className="hover:bg-secondary/20 transition-colors">
                                            <td className="px-6 py-4 font-medium text-foreground">{nameFor(r.employeeId)}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-muted-foreground">{fmtDate(r.periodStart)} – {fmtDate(r.periodEnd)}</td>
                                            <td className="px-4 py-4 text-right">{money(r.grossSalary)}</td>
                                            <td className="px-4 py-4 text-right text-destructive/80">{money(sumVals(r.deductions))}</td>
                                            <td className="px-4 py-4 text-right font-semibold text-primary">{money(r.netSalary)}</td>
                                            <td className="px-4 py-4"><Badge variant={statusVariant(r.status)}>{r.status.toUpperCase()}</Badge></td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {r.status === 'draft' && (
                                                        <Button variant="secondary" size="sm" isLoading={actingId === r.id}
                                                            onClick={() => handleApprove(r.id)}>Approve</Button>
                                                    )}
                                                    {r.status === 'processed' && (
                                                        <Button variant="secondary" size="sm" onClick={() => openMarkPaid(r)}>Mark Paid</Button>
                                                    )}
                                                    <Button variant="ghost" size="sm" className="text-primary" onClick={() => setSlip(r)}>View Slip</Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Generate modal */}
            <Dialog open={isFormOpen} onOpenChange={setFormOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Generate Payroll</DialogTitle>
                        <DialogDescription>Create a payroll record for an employee and pay period.</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Employee</label>
                            <Select value={form.employeeId} onValueChange={onEmployeeChange}>
                                <SelectTrigger invalid={!!errors.employeeId}>
                                    <SelectValue placeholder="Select employee" />
                                </SelectTrigger>
                                <SelectContent>
                                    {employees.map((e) => (
                                        <SelectItem key={e.id} value={e.id}>
                                            {e.displayName || `${e.firstName} ${e.lastName}`} · {e.employeeCode}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {errors.employeeId && <p className="text-xs text-destructive">{errors.employeeId}</p>}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Period Start</label>
                                <Input type="date" value={form.periodStart}
                                    onChange={(e) => setForm((f) => ({ ...f, periodStart: e.target.value }))} invalid={!!errors.periodStart} />
                                {errors.periodStart && <p className="text-xs text-destructive">{errors.periodStart}</p>}
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Period End</label>
                                <Input type="date" value={form.periodEnd}
                                    onChange={(e) => setForm((f) => ({ ...f, periodEnd: e.target.value }))} invalid={!!errors.periodEnd} />
                                {errors.periodEnd && <p className="text-xs text-destructive">{errors.periodEnd}</p>}
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Basic Salary</label>
                            <Input type="number" min={0} value={form.basicSalary}
                                onChange={(e) => setForm((f) => ({ ...f, basicSalary: e.target.value }))} invalid={!!errors.basicSalary} />
                            {errors.basicSalary && <p className="text-xs text-destructive">{errors.basicSalary}</p>}
                        </div>

                        <div>
                            <p className="text-xs font-semibold text-success mb-2">Allowances</p>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="space-y-1"><label className="text-xs text-muted-foreground">Housing</label>
                                    <Input type="number" min={0} value={form.housing} onChange={(e) => setForm((f) => ({ ...f, housing: e.target.value }))} /></div>
                                <div className="space-y-1"><label className="text-xs text-muted-foreground">Transport</label>
                                    <Input type="number" min={0} value={form.transport} onChange={(e) => setForm((f) => ({ ...f, transport: e.target.value }))} /></div>
                                <div className="space-y-1"><label className="text-xs text-muted-foreground">Other</label>
                                    <Input type="number" min={0} value={form.allowOther} onChange={(e) => setForm((f) => ({ ...f, allowOther: e.target.value }))} /></div>
                            </div>
                        </div>

                        <div>
                            <p className="text-xs font-semibold text-destructive mb-2">Deductions</p>
                            <div className="grid grid-cols-4 gap-3">
                                <div className="space-y-1"><label className="text-xs text-muted-foreground">Tax</label>
                                    <Input type="number" min={0} value={form.tax} onChange={(e) => setForm((f) => ({ ...f, tax: e.target.value }))} /></div>
                                <div className="space-y-1"><label className="text-xs text-muted-foreground">Insurance</label>
                                    <Input type="number" min={0} value={form.insurance} onChange={(e) => setForm((f) => ({ ...f, insurance: e.target.value }))} /></div>
                                <div className="space-y-1"><label className="text-xs text-muted-foreground">Retirement</label>
                                    <Input type="number" min={0} value={form.retirement} onChange={(e) => setForm((f) => ({ ...f, retirement: e.target.value }))} /></div>
                                <div className="space-y-1"><label className="text-xs text-muted-foreground">Other</label>
                                    <Input type="number" min={0} value={form.dedOther} onChange={(e) => setForm((f) => ({ ...f, dedOther: e.target.value }))} /></div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between rounded-lg border border-border bg-surface-2 px-4 py-3">
                            <div>
                                <p className="text-xs text-muted-foreground">Gross</p>
                                <p className="text-lg font-semibold text-foreground">{money(preview.gross)}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-muted-foreground">Net</p>
                                <p className="text-lg font-semibold text-primary">{money(preview.net)}</p>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Notes</label>
                            <Textarea rows={2} value={form.notes}
                                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Optional notes…" />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
                        <Button variant="primary" onClick={handleGenerate} isLoading={saving}>Generate</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Mark paid modal */}
            <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
                <DialogContent className="max-w-[460px]">
                    <DialogHeader>
                        <DialogTitle>Mark as Paid</DialogTitle>
                        <DialogDescription>
                            Record the payment for <span className="font-medium text-foreground">{payTarget ? nameFor(payTarget.employeeId) : ''}</span>
                            {payTarget ? ` — ${money(payTarget.netSalary)}` : ''}.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Payment Date</label>
                            <Input type="date" value={payForm.paymentDate}
                                onChange={(e) => setPayForm((f) => ({ ...f, paymentDate: e.target.value }))} />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Payment Reference</label>
                            <Input value={payForm.paymentReference}
                                onChange={(e) => setPayForm((f) => ({ ...f, paymentReference: e.target.value }))} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setPayTarget(null)} disabled={paying}>Cancel</Button>
                        <Button variant="primary" onClick={handleMarkPaid} isLoading={paying}>Confirm Payment</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Payslip dialog (real data from the record) */}
            <Dialog open={!!slip} onOpenChange={(o) => !o && setSlip(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Payslip</DialogTitle>
                        <DialogDescription>
                            {slip ? `${nameFor(slip.employeeId)} · ${fmtDate(slip.periodStart)} – ${fmtDate(slip.periodEnd)}` : ''}
                        </DialogDescription>
                    </DialogHeader>
                    {slip && (
                        <div className="grid grid-cols-2 gap-0 border border-border rounded-lg overflow-hidden">
                            <div className="border-r border-border">
                                <div className="px-4 py-2 bg-success/10 border-b border-border"><p className="text-xs font-semibold text-success">Earnings</p></div>
                                <Row label="Basic Salary" value={slip.basicSalary} />
                                <Row label="Housing" value={slip.allowances.housing} />
                                <Row label="Transport" value={slip.allowances.transport} />
                                <Row label="Other" value={slip.allowances.other} />
                                <div className="flex justify-between px-4 py-3 bg-success/5 text-sm font-semibold">
                                    <span className="text-success">Gross</span><span className="text-success">{money(slip.grossSalary)}</span>
                                </div>
                            </div>
                            <div>
                                <div className="px-4 py-2 bg-destructive/10 border-b border-border"><p className="text-xs font-semibold text-destructive">Deductions</p></div>
                                <Row label="Tax" value={slip.deductions.tax} />
                                <Row label="Insurance" value={slip.deductions.insurance} />
                                <Row label="Retirement" value={slip.deductions.retirement} />
                                <Row label="Other" value={slip.deductions.other} />
                                <div className="flex justify-between px-4 py-3 bg-destructive/5 text-sm font-semibold">
                                    <span className="text-destructive">Total</span><span className="text-destructive">{money(sumVals(slip.deductions))}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    {slip && (
                        <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg text-center">
                            <p className="text-xs text-muted-foreground font-semibold mb-1">Net Salary</p>
                            <p className="text-primary font-display text-metric">{money(slip.netSalary)}</p>
                            {slip.status === 'paid' && slip.paymentDate && (
                                <p className="text-xs text-muted-foreground mt-2">Paid {fmtDate(slip.paymentDate)} · Ref {slip.paymentReference}</p>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Row({ label, value }: { label: string; value?: number }) {
    return (
        <div className="flex justify-between px-4 py-2 border-b border-border text-sm last:border-0">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-semibold text-foreground">${(Number(value) || 0).toLocaleString()}</span>
        </div>
    );
}
